import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */

export interface ProductCartItem {
  listingId: string;
  name: string;
  rate: number;
  qty: number;
}

interface ProductServiceFeeState {
  items: ProductCartItem[];

  subtotal: number;
  totalQty: number;

  guestsFee: number;
  hostsFee: number;
  hostProfit: number;
  platformProfit: number;
  totalCharged: number;

  calculated: boolean;
}

/* ─────────────────────────────────────────────
   UTILITY FUNCTIONS
───────────────────────────────────────────── */

export const calculateHostEarnings = (total: number) => {
  const hostFeePercentage = 0.20;
  const hostsFee = total * hostFeePercentage;
  const hostProfit = parseFloat((total - hostsFee).toFixed(2));
  return { hostProfit, hostsFee };
};

export const calculateGuestServiceFee = (subtotal: number): number => {
  let guestsFee: number;
  if (subtotal > 5000) guestsFee = subtotal * 0.2;
  else if (subtotal > 1000) guestsFee = subtotal * 0.2;
  else if (subtotal > 500) guestsFee = subtotal * 0.12;
  else if (subtotal > 100) guestsFee = subtotal * 0.1;
  else guestsFee = subtotal * 0.08;
  return parseFloat(guestsFee.toFixed(2));
};

export const calculatePlatformProfit = (
  hostsFee: number,
  guestsFee: number
): number =>
  parseFloat((hostsFee + guestsFee).toFixed(2));

/* ─────────────────────────────────────────────
   ASYNC CALCULATION THUNK
   (name kept exactly as requested)
───────────────────────────────────────────── */

export const calculateProductsEarnings = createAsyncThunk(
  "productServiceFee/calculateProductsEarnings",
  async ({ items }: { items: ProductCartItem[] }) => {
    const totalQty = items.reduce((s, i) => s + i.qty, 0);

    const subtotal = items.reduce(
      (s, i) => s + i.qty * i.rate,
      0
    );

    const { hostProfit, hostsFee } = calculateHostEarnings(subtotal);
    const guestsFee = calculateGuestServiceFee(subtotal);
    const platformProfit = calculatePlatformProfit(hostsFee, guestsFee);

    const totalCharged = parseFloat(
      (subtotal + guestsFee).toFixed(2)
    );

    return {
      items,
      totalQty,
      subtotal,

      guestsFee,
      hostsFee,
      hostProfit,
      platformProfit,
      totalCharged,
    };
  }
);

/* ─────────────────────────────────────────────
   SLICE
───────────────────────────────────────────── */

const initialState: ProductServiceFeeState = {
  items: [],
  subtotal: 0,
  totalQty: 0,

  guestsFee: 0,
  hostsFee: 0,
  hostProfit: 0,
  platformProfit: 0,
  totalCharged: 0,

  calculated: false,
};

const productServiceFeeSlice = createSlice({
  name: "productServiceFee",
  initialState,
  reducers: {
    resetProductServiceFees: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(
      calculateProductsEarnings.fulfilled,
      (state, action) => {
        state.items = action.payload.items;
        state.totalQty = action.payload.totalQty;
        state.subtotal = action.payload.subtotal;

        state.guestsFee = action.payload.guestsFee;
        state.hostsFee = action.payload.hostsFee;
        state.hostProfit = action.payload.hostProfit;
        state.platformProfit = action.payload.platformProfit;
        state.totalCharged = action.payload.totalCharged;

        state.calculated = true;
      }
    );
  },
});

export const { resetProductServiceFees } = productServiceFeeSlice.actions;

export default productServiceFeeSlice.reducer;